import {Injectable} from '@angular/core';
import {IFilterOption, SexFilter, StateFilter} from '@shared/components/filter/filter';
import {FilterService} from '@shared/components/filter/filter.service';
import {CountryService} from '@core/services/country.service';
import { StateHttpService } from '@api/http/state/state-http.service';
import { EventsHttpService } from '@api/http/events/events-http.service';
import { Event } from '@api/models/event/event';

export enum GamesFilter {
  GLOBAL = 'global',
  INTERNATIONAL = 'international',
  STATE = 'state',
  USER = 'user'
}

@Injectable()
export class GamesService {
  public loading = false;
  public moreMatchesAvailable = false;
  public selectedDate: Date;
  private filter: string;
  private stateId: string = null;
  public events: Event[] = [];
  private readonly pageSize = 16;
  public readonly sexFilter: SexFilter;
  public readonly stateFilter: StateFilter;

  constructor( private stateHttpService: StateHttpService,
               private filterService: FilterService,
               private eventsHttpService: EventsHttpService) {

    this.sexFilter = this.filterService.create(SexFilter);
    this.stateFilter = this.filterService.create(StateFilter);
    this.stateFilter.optionFactory = this.createStateFilterOptions;
  }

  async createStateFilterOptions(): Promise<IFilterOption[]> {
    const states = await this.stateHttpService.getStatesForCountry(CountryService.austrianId);
    const options = states.map(state => {
      return {
        value: state._id,
        label: state.name
      }
    });

    options.unshift({ value: 'international', label: 'International'});
    return options;
  }

  public async onFilterChange() {
    await this.loadEvents();
  }

  public async loadEvents(keepEvents = false, loadUserEvents = false) {
    this.loading = !keepEvents;
    this.setFilterParam(loadUserEvents);
    const pageSize = this.events.length === 0 || !keepEvents ? this.pageSize : this.events.length;
    try {
      this.events = [];
      await this.fetchEvents(this.filter, this.selectedDate, 0, pageSize);
      this.loading = false;
    } catch (e) {
      this.loading = false;
      throw e;
    }
  }

  public async loadMoreEvents() {
    this.loading = true;
    try {
      await this.fetchEvents(this.filter, this.selectedDate, this.events.length);
      this.loading = false;
    } catch (e) {
      this.loading = false;
      throw e;
    }
  }

  private async fetchEvents(filter: string, date: Date, skip: number, pageSize = this.pageSize): Promise<void> {
    const response = await this.eventsHttpService.getEventsByDate({
      filter,
      date,
      skip,
      limit: pageSize,
      entityId: this.stateId,
      sex: this.sexFilter.selectedOptionValue
    });
    if (response?.data) {
      this.events = this.events.concat(response.data);
      this.moreMatchesAvailable = this.events.length < response.total_count;
    }
  }

  private setFilterParam(loadUserEvents: boolean): void {
    this.stateId = null;
    if (loadUserEvents) {
      this.filter = GamesFilter.USER;
    } else if (this.stateFilter.active) {
      if (this.stateFilter.selectedOptionValue === GamesFilter.INTERNATIONAL) {
        this.filter = GamesFilter.INTERNATIONAL;
      } else {
        this.filter = GamesFilter.STATE;
        this.stateId = this.stateFilter.selectedOptionValue;
      }
    } else {
      this.filter = GamesFilter.GLOBAL;
    }
  }

  public isFilterActive(): boolean {
    return this.sexFilter.active || this.stateFilter.active;
  }
}
